import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import type { PaginatedRequestsResponse } from "./useStructuredRequestsPaginated";

import { ENDPOINTS } from "@/api/application-api";
import { queryKeys } from "@/constants";
import type { Request } from "@/types/requests";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export type RequestLogHydrationStatus = "pending" | "complete" | "error";

export type RequestLogListItem = Request & {
  hydration_status?: RequestLogHydrationStatus;
};

export interface RequestLogHydrationResponse {
  success: boolean;
  items: Request[];
}

interface FetchRequestsHydrateArgs {
  userToken: string;
  workspaceId: number;
  requestLogIds: number[];
  includePromptName?: boolean;
  sourceFieldsByRequestId?: Record<string, Record<string, unknown>>;
  signal?: AbortSignal;
}

interface UseRequestLogHydrationArgs {
  userToken: string;
  workspaceId: number;
  requestLogIds: number[];
  sourceFieldsByRequestId?: Record<string, Record<string, unknown>>;
  enabled?: boolean;
}

const REQUEST_LOG_HYDRATION_CACHE_MS = 5 * 60 * 1000;

export const getRequestLogHydrationQueryKeyPrefix = (workspaceId: number) =>
  [queryKeys.requests, "hydrate", workspaceId] as const;

export const getRequestLogHydrationQueryKey = ({
  workspaceId,
  requestLogIds,
  sourceFieldsByRequestId,
}: {
  workspaceId: number;
  requestLogIds: number[];
  sourceFieldsByRequestId?: Record<string, Record<string, unknown>>;
}) =>
  [
    ...getRequestLogHydrationQueryKeyPrefix(workspaceId),
    requestLogIds.join(","),
    JSON.stringify(sourceFieldsByRequestId ?? {}),
  ] as const;

export const fetchRequestsHydrate = async ({
  userToken,
  workspaceId,
  requestLogIds,
  includePromptName = true,
  sourceFieldsByRequestId,
  signal,
}: FetchRequestsHydrateArgs): Promise<RequestLogHydrationResponse> => {
  const response = await fetch(ENDPOINTS.post_requests_hydrate(workspaceId), {
    method: "POST",
    headers: jsonAuthHeaders(userToken),
    body: JSON.stringify({
      request_log_ids: requestLogIds,
      include_prompt_name: includePromptName,
      source_fields_by_request_id: sourceFieldsByRequestId ?? {},
    }),
    signal,
  });

  return handleResponse(response);
};

export const buildRequestLogStubItems = (
  response: Pick<
    PaginatedRequestsResponse,
    "items" | "request_log_ids" | "source_fields_by_request_id"
  >,
): RequestLogListItem[] => {
  const sourceFields = response.source_fields_by_request_id ?? {};
  const ids =
    response.request_log_ids ??
    response.items.map(item => Number(item.id)).filter(id => Number.isFinite(id));

  return ids.map(requestId => {
    const existing = response.items.find(item => Number(item.id) === requestId);
    const fields = sourceFields[String(requestId)] ?? {};
    return {
      ...(existing ?? { id: requestId, type: "request" as const }),
      ...fields,
      id: requestId,
      type: "request" as const,
      hydration_status: "pending",
    } as RequestLogListItem;
  });
};

export const applyRequestLogHydrationToItems = ({
  stubs,
  hydratedItems,
  status,
}: {
  stubs: RequestLogListItem[];
  hydratedItems?: Request[];
  status: RequestLogHydrationStatus;
}): RequestLogListItem[] => {
  if (!hydratedItems || hydratedItems.length === 0) {
    return stubs.map(item => ({ ...item, hydration_status: status }));
  }

  const hydratedById = new Map(hydratedItems.map(item => [Number(item.id), item]));
  return stubs.map(stub => {
    const hydrated = hydratedById.get(Number(stub.id));
    if (!hydrated) {
      return { ...stub, hydration_status: status };
    }
    return {
      ...stub,
      ...hydrated,
      hydration_status: status,
    } as RequestLogListItem;
  });
};

export const useRequestLogHydration = ({
  userToken,
  workspaceId,
  requestLogIds,
  sourceFieldsByRequestId,
  enabled = true,
}: UseRequestLogHydrationArgs) => {
  const uniqueIds = useMemo(
    () =>
      Array.from(new Set(requestLogIds.filter(id => Number.isFinite(id)))).sort((a, b) => a - b),
    [requestLogIds],
  );
  const queryEnabled = enabled && !!userToken && workspaceId > 0 && uniqueIds.length > 0;

  return useQuery({
    queryKey: getRequestLogHydrationQueryKey({
      workspaceId,
      requestLogIds: uniqueIds,
      sourceFieldsByRequestId,
    }),
    queryFn: () =>
      fetchRequestsHydrate({
        userToken,
        workspaceId,
        requestLogIds: uniqueIds,
        sourceFieldsByRequestId,
      }),
    enabled: queryEnabled,
    staleTime: REQUEST_LOG_HYDRATION_CACHE_MS,
  });
};
